import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePlanInput,
  FeatureDescriptorDto,
  LimitDescriptorDto,
  LimitValue,
  OverusePolicy,
  PlanDescriptorsDto,
  PlanDto,
  PlanPolicies,
  PlanPolicyDescriptorDto,
  ServicePolicyValue,
} from '@cpm/shared';
import {
  formatLimitAmount,
  OVERUSE_POLICIES,
  OVERUSE_POLICY_LABELS,
  POLICY_ENFORCEMENT_ACTIONS,
  POLICY_ENFORCEMENT_LABELS,
} from '@cpm/shared';
import { Layers, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api/client.js';
import { HelpLink } from '../components/help.js';
import { Modal } from '../components/Modal.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Spinner,
} from '../components/ui.js';

/**
 * The plan editor renders from the descriptor registry (ADR-0011): which
 * limits and features exist — and how they group and format — comes from
 * GET /api/plans/descriptors, so module-contributed keys appear without
 * touching this page. Descriptors of disabled modules render read-only;
 * values with no descriptor at all (module uninstalled) are preserved and
 * shown read-only too.
 */

/** Group limit descriptors by their sort group, keeping first-seen order. */
function groupLimits(descriptors: LimitDescriptorDto[]): Array<[string, LimitDescriptorDto[]]> {
  const groups = new Map<string, LimitDescriptorDto[]>();
  for (const descriptor of descriptors) {
    const group = groups.get(descriptor.group) ?? [];
    group.push(descriptor);
    groups.set(descriptor.group, group);
  }
  return [...groups.entries()];
}

function LimitInput({
  descriptor,
  value,
  onChange,
}: {
  descriptor: LimitDescriptorDto;
  value: LimitValue;
  onChange: (v: LimitValue) => void;
}) {
  const unlimited = value === null;
  const readOnly = !descriptor.active;
  const label =
    descriptor.kind === 'megabytes' ? `${descriptor.label} (MB)` : descriptor.label;
  return (
    <div title={descriptor.hint}>
      <Label>
        {label}
        {readOnly && (
          <span className="ml-1.5 font-normal text-slate-400" title={`The ${descriptor.moduleName} module is disabled — value preserved`}>
            (read-only)
          </span>
        )}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          disabled={unlimited || readOnly}
          value={unlimited ? '' : value}
          placeholder={unlimited ? 'Unlimited' : undefined}
          onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        />
        <label className="flex shrink-0 items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            className="size-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
            checked={unlimited}
            disabled={readOnly}
            onChange={(e) => onChange(e.target.checked ? null : 0)}
          />
          ∞
        </label>
      </div>
    </div>
  );
}

/** Values whose module is gone entirely: preserved, shown, never edited. */
function OrphanedValues({ entries }: { entries: Array<[string, string]> }) {
  if (entries.length === 0) return null;
  return (
    <div className="rounded-md border border-dashed border-slate-200 p-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
      <p className="mb-1 font-medium">Preserved values from unavailable modules</p>
      {entries.map(([key, value]) => (
        <p key={key} className="tabular-nums">
          <code>{key}</code>: {value}
        </p>
      ))}
    </div>
  );
}

function PolicyInput({
  descriptor,
  value,
  onChange,
}: {
  descriptor: PlanPolicyDescriptorDto;
  value: ServicePolicyValue;
  onChange: (value: ServicePolicyValue) => void;
}) {
  const disabled = !descriptor.active;
  const enforcement = (
    <div>
      <Label>Action after grace</Label>
      <select
        className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
        disabled={disabled}
        value={value.enforcement}
        onChange={(event) =>
          onChange({
            ...value,
            enforcement: event.target.value as ServicePolicyValue['enforcement'],
          })
        }
      >
        {POLICY_ENFORCEMENT_ACTIONS.map((action) => (
          <option key={action} value={action}>
            {POLICY_ENFORCEMENT_LABELS[action].label}
          </option>
        ))}
      </select>
    </div>
  );

  if (value.kind === 'expiry') {
    return (
      <div className="rounded-md border border-slate-200 p-3 dark:border-slate-700">
        <h4 className="mb-2 text-sm font-medium">{descriptor.label}</h4>
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label>Policy</Label>
            <select
              className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
              disabled={disabled}
              value={value.mode}
              onChange={(event) => {
                const mode = event.target.value as typeof value.mode;
                onChange({
                  ...value,
                  mode,
                  duration: mode === 'duration' ? (value.duration ?? { value: 1, unit: 'months' }) : null,
                  expiresOn: mode === 'date' ? (value.expiresOn ?? '2030-01-01') : null,
                });
              }}
            >
              <option value="unlimited">No expiry</option>
              <option value="duration">Calendar duration</option>
              <option value="date">Exact date</option>
            </select>
          </div>
          {value.mode === 'duration' && (
            <>
              <div>
                <Label>Duration</Label>
                <Input
                  type="number"
                  min={1}
                  disabled={disabled}
                  value={value.duration?.value ?? 1}
                  onChange={(event) =>
                    onChange({
                      ...value,
                      duration: {
                        value: Number(event.target.value),
                        unit: value.duration?.unit ?? 'months',
                      },
                    })
                  }
                />
              </div>
              <div>
                <Label>Unit</Label>
                <select
                  className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
                  value={value.duration?.unit ?? 'months'}
                  disabled={disabled}
                  onChange={(event) =>
                    onChange({
                      ...value,
                      duration: {
                        value: value.duration?.value ?? 1,
                        unit: event.target.value as 'days' | 'months' | 'years',
                      },
                    })
                  }
                >
                  <option value="days">days</option>
                  <option value="months">months</option>
                  <option value="years">years</option>
                </select>
              </div>
            </>
          )}
          {value.mode === 'date' && (
            <div>
              <Label>Expires on</Label>
              <Input
                type="date"
                disabled={disabled}
                value={value.expiresOn ?? ''}
                onChange={(event) => onChange({ ...value, expiresOn: event.target.value })}
              />
            </div>
          )}
          <div>
            <Label>Warn days before</Label>
            <Input
              type="number"
              min={0}
              disabled={disabled}
              value={value.warningDays ?? ''}
              placeholder={value.warningOn ?? 'No warning'}
              onChange={(event) =>
                onChange({
                  ...value,
                  warningDays: event.target.value === '' ? null : Number(event.target.value),
                  warningOn: null,
                })
              }
            />
          </div>
          <div>
            <Label>Time zone</Label>
            <Input
              disabled={disabled}
              value={value.timeZone}
              onChange={(event) => onChange({ ...value, timeZone: event.target.value })}
            />
          </div>
          <div>
            <Label>Grace (hours)</Label>
            <Input
              type="number"
              min={0}
              disabled={disabled}
              value={value.graceHours}
              onChange={(event) => onChange({ ...value, graceHours: Number(event.target.value) })}
            />
          </div>
          {enforcement}
        </div>
      </div>
    );
  }

  if (value.kind === 'monthly-transfer') {
    return (
      <div className="rounded-md border border-slate-200 p-3 dark:border-slate-700">
        <h4 className="mb-2 text-sm font-medium">{descriptor.label}</h4>
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label>Allowance (bytes, blank = unlimited)</Label>
            <Input
              type="number"
              min={0}
              disabled={disabled}
              value={value.allowanceBytes ?? ''}
              onChange={(event) =>
                onChange({
                  ...value,
                  allowanceBytes: event.target.value === '' ? null : Number(event.target.value),
                })
              }
            />
          </div>
          <div>
            <Label>Soft threshold (bytes)</Label>
            <Input
              type="number"
              min={0}
              disabled={disabled}
              value={value.softThresholdBytes ?? ''}
              onChange={(event) =>
                onChange({
                  ...value,
                  softThresholdBytes:
                    event.target.value === '' ? null : Number(event.target.value),
                })
              }
            />
          </div>
          <div>
            <Label>Billing anchor (1–28)</Label>
            <Input
              type="number"
              min={1}
              max={28}
              disabled={disabled}
              value={value.billingAnchorDay}
              onChange={(event) =>
                onChange({ ...value, billingAnchorDay: Number(event.target.value) })
              }
            />
          </div>
          <div>
            <Label>Time zone</Label>
            <Input
              disabled={disabled}
              value={value.timeZone}
              onChange={(event) => onChange({ ...value, timeZone: event.target.value })}
            />
          </div>
          <div>
            <Label>Grace (hours)</Label>
            <Input
              type="number"
              min={0}
              disabled={disabled}
              value={value.graceHours}
              onChange={(event) => onChange({ ...value, graceHours: Number(event.target.value) })}
            />
          </div>
          {enforcement}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 p-3 dark:border-slate-700">
      <h4 className="mb-2 text-sm font-medium">{descriptor.label}</h4>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label>Threshold (bytes, blank = disabled)</Label>
          <Input
            type="number"
            min={0}
            disabled={disabled}
            value={value.thresholdBytes ?? ''}
            onChange={(event) =>
              onChange({
                ...value,
                thresholdBytes: event.target.value === '' ? null : Number(event.target.value),
              })
            }
          />
        </div>
        <div>
          <Label>Grace (hours)</Label>
          <Input
            type="number"
            min={0}
            disabled={disabled}
            value={value.graceHours}
            onChange={(event) => onChange({ ...value, graceHours: Number(event.target.value) })}
          />
        </div>
        {enforcement}
      </div>
    </div>
  );
}

function PlanForm({
  descriptors,
  initial,
  onSubmit,
  pending,
  error,
  submitLabel,
}: {
  descriptors: PlanDescriptorsDto;
  initial?: PlanDto;
  onSubmit: (input: CreatePlanInput) => void;
  pending: boolean;
  error: unknown;
  submitLabel: string;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  // Seed registered keys from the plan (absent = unlimited/disabled, the
  // read semantics) or from descriptor defaults for a new plan; carry
  // unregistered keys through untouched so submits never drop them.
  const [limits, setLimits] = useState<CreatePlanInput['limits']>(() => {
    const seed: CreatePlanInput['limits'] = { ...initial?.limits };
    for (const d of descriptors.limits) {
      seed[d.key] = initial ? (initial.limits[d.key] ?? null) : d.default;
    }
    return seed;
  });
  const [features, setFeatures] = useState<CreatePlanInput['features']>(() => {
    const seed: CreatePlanInput['features'] = { ...initial?.features };
    for (const d of descriptors.features) {
      seed[d.key] = initial ? (initial.features[d.key] ?? false) : d.default;
    }
    return seed;
  });
  const [policies, setPolicies] = useState<PlanPolicies>(() => {
    const seed: PlanPolicies = { ...initial?.policies };
    for (const descriptor of descriptors.policies) {
      seed[descriptor.key] = initial?.policies[descriptor.key] ?? descriptor.default;
    }
    return seed;
  });
  const [overusePolicy, setOverusePolicy] = useState<OverusePolicy>(
    initial?.overusePolicy ?? 'notify',
  );
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  const knownLimits = new Set(descriptors.limits.map((d) => d.key));
  const knownFeatures = new Set(descriptors.features.map((d) => d.key));
  const knownPolicies = new Set(descriptors.policies.map((d) => d.key));
  const orphaned: Array<[string, string]> = [
    ...Object.entries(limits)
      .filter(([key]) => !knownLimits.has(key))
      .map(([key, value]) => [key, value === null ? 'Unlimited' : String(value)] as [string, string]),
    ...Object.entries(features)
      .filter(([key]) => !knownFeatures.has(key))
      .map(([key, value]) => [key, value ? 'enabled' : 'disabled'] as [string, string]),
    ...Object.entries(policies)
      .filter(([key]) => !knownPolicies.has(key))
      .map(([key, value]) => [key, JSON.stringify(value)] as [string, string]),
  ];

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ name, description: description || null, limits, features, policies, overusePolicy, isActive });
      }}
      className="space-y-5"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="plan-name">Name</Label>
          <Input id="plan-name" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="plan-desc">Description</Label>
          <Input id="plan-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </div>

      {groupLimits(descriptors.limits).map(([group, groupDescriptors]) => (
        <div key={group}>
          <h3 className="mb-2 text-sm font-semibold">{group}</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            {groupDescriptors.map((descriptor) => (
              <LimitInput
                key={descriptor.key}
                descriptor={descriptor}
                value={limits[descriptor.key] ?? null}
                onChange={(v) => setLimits({ ...limits, [descriptor.key]: v })}
              />
            ))}
          </div>
        </div>
      ))}

      <div>
        <h3 className="mb-2 text-sm font-semibold">Lifecycle &amp; usage policies</h3>
        <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
          These values are distinct from resource-count and megabyte limits. Every action defaults
          to notify only; blocking or suspension must be selected explicitly.
        </p>
        <div className="space-y-3">
          {descriptors.policies.map((descriptor) => (
            <PolicyInput
              key={descriptor.key}
              descriptor={descriptor}
              value={policies[descriptor.key] ?? descriptor.default}
              onChange={(value) => setPolicies({ ...policies, [descriptor.key]: value })}
            />
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold">Overuse policy</h3>
        <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
          What happens when a subscription's measured usage crosses a quota (currently
          evaluated for disk). "Not allowed" flags the subscription over quota and suspends it
          automatically — see <HelpLink topic="metrics/overview">Usage metrics &amp; quotas</HelpLink>.
        </p>
        <div className="space-y-2">
          {OVERUSE_POLICIES.map((policy) => (
            <label key={policy} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="overuse-policy"
                className="mt-0.5 size-4 border-slate-300 text-brand-600 focus:ring-brand-600"
                checked={overusePolicy === policy}
                onChange={() => setOverusePolicy(policy)}
              />
              <span>
                <span className="font-medium">{OVERUSE_POLICY_LABELS[policy].label}</span>
                <span className="block text-xs text-slate-500 dark:text-slate-400">
                  {OVERUSE_POLICY_LABELS[policy].description}
                </span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold">Tenant permissions</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          {descriptors.features.map((descriptor: FeatureDescriptorDto) => (
            <label
              key={descriptor.key}
              className="flex items-center gap-2 text-sm"
              title={descriptor.description}
            >
              <input
                type="checkbox"
                className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                checked={features[descriptor.key] ?? false}
                disabled={!descriptor.active}
                onChange={(e) => setFeatures({ ...features, [descriptor.key]: e.target.checked })}
              />
              {descriptor.label}
              {!descriptor.active && <span className="text-xs text-slate-400">(read-only)</span>}
            </label>
          ))}
        </div>
      </div>

      <OrphanedValues entries={orphaned} />

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
        />
        Plan is active (assignable to new subscriptions)
      </label>

      {error != null && <ErrorNote error={error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={pending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

export function PlansPage() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<PlanDto | null>(null);

  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.get<PlanDto[]>('/api/plans/'),
  });

  const descriptors = useQuery({
    queryKey: ['plan-descriptors'],
    queryFn: () => api.get<PlanDescriptorsDto>('/api/plans/descriptors'),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['plans'] });

  const create = useMutation({
    mutationFn: (input: CreatePlanInput) => api.post<PlanDto>('/api/plans/', input),
    onSuccess: () => {
      invalidate();
      setCreateOpen(false);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreatePlanInput }) =>
      api.patch<PlanDto>(`/api/plans/${id}`, input),
    onSuccess: () => {
      invalidate();
      setEditing(null);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/plans/${id}`),
    onSuccess: invalidate,
  });

  return (
    <div>
      <PageHeader
        title="Service Plans"
        description="Reusable bundles of limits and permissions, Plesk-style."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> New plan
          </Button>
        }
      />
      {remove.error != null && (
        <div className="mb-4">
          <ErrorNote error={remove.error} />
        </div>
      )}

      {plans.isPending || descriptors.isPending ? (
        <Spinner />
      ) : plans.error ? (
        <ErrorNote error={plans.error} />
      ) : descriptors.error ? (
        <ErrorNote error={descriptors.error} />
      ) : plans.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Layers className="size-10" />}
            title="No plans yet"
            description="Plans define what a subscription may use — applications, databases, disk, and tenant permissions."
            action={
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="size-4" /> New plan
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {plans.data.map((plan) => (
            <Card key={plan.id} className="flex flex-col p-5">
              <div className="mb-3 flex items-start justify-between gap-2">
                <div>
                  <h2 className="font-semibold">{plan.name}</h2>
                  {plan.description && (
                    <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{plan.description}</p>
                  )}
                </div>
                {!plan.isActive && <Badge tone="gray">inactive</Badge>}
              </div>
              <dl className="mb-4 grid flex-1 grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                {descriptors.data.limits.map((descriptor) => (
                  <div key={descriptor.key} className="flex justify-between gap-2">
                    <dt className="text-slate-500 dark:text-slate-400">{descriptor.label}</dt>
                    <dd className="font-medium tabular-nums">
                      {formatLimitAmount(plan.limits[descriptor.key], descriptor.kind)}
                    </dd>
                  </div>
                ))}
                <div className="col-span-2 flex justify-between gap-2 border-t border-slate-100 pt-1.5 dark:border-slate-800">
                  <dt className="text-slate-500 dark:text-slate-400">Overuse</dt>
                  <dd
                    className="text-right font-medium"
                    title={OVERUSE_POLICY_LABELS[plan.overusePolicy].description}
                  >
                    {OVERUSE_POLICY_LABELS[plan.overusePolicy].label}
                  </dd>
                </div>
              </dl>
              <div className="flex items-center justify-between border-t border-slate-100 pt-3 text-sm dark:border-slate-800">
                <span className="text-slate-500 dark:text-slate-400">
                  {plan.subscriptionCount} subscription{plan.subscriptionCount === 1 ? '' : 's'}
                </span>
                <div className="flex gap-1">
                  <Button variant="ghost" onClick={() => setEditing(plan)} aria-label={`Edit ${plan.name}`}>
                    <Pencil className="size-4" />
                  </Button>
                  {plan.subscriptionCount === 0 && (
                    <Button
                      variant="ghost"
                      className="hover:!text-red-600"
                      onClick={() => {
                        if (window.confirm(`Delete plan "${plan.name}"?`)) remove.mutate(plan.id);
                      }}
                      aria-label={`Delete ${plan.name}`}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal title="New service plan" open={createOpen} onClose={() => setCreateOpen(false)} wide>
        {descriptors.data && createOpen && (
          <PlanForm
            descriptors={descriptors.data}
            onSubmit={(input) => create.mutate(input)}
            pending={create.isPending}
            error={create.error}
            submitLabel="Create plan"
          />
        )}
      </Modal>

      <Modal title={`Edit ${editing?.name ?? 'plan'}`} open={editing !== null} onClose={() => setEditing(null)} wide>
        {descriptors.data && editing && (
          <PlanForm
            descriptors={descriptors.data}
            initial={editing}
            onSubmit={(input) => update.mutate({ id: editing.id, input })}
            pending={update.isPending}
            error={update.error}
            submitLabel="Save changes"
          />
        )}
      </Modal>
    </div>
  );
}
